import { useCallback, useEffect, useRef } from "react";

interface DebouncedCallback<A extends unknown[]> {
  /** Schedules the callback, replacing any pending call. */
  run: (...args: A) => void;
  /** Drops a pending call without running it. */
  cancel: () => void;
  /**
   * Runs a pending call now, with the arguments it was scheduled with, rather
   * than after the delay. Returns whether there was one to run.
   */
  flush: () => boolean;
}

/**
 * Debounces a callback: `run` fires it after `delayMs` of quiet, and each call
 * resets the timer. `cancel` drops a pending call (e.g. before an action that
 * supersedes it), `flush` runs it early (e.g. before an action that should
 * include it), and unmounting cancels automatically. The latest `callback` is
 * always used, so `run`/`cancel`/`flush` keep a stable identity.
 */
export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delayMs: number,
): DebouncedCallback<A> {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The arguments of the pending call, so `flush` can run it; cleared once the
  // call runs or is cancelled.
  const pendingArgs = useRef<A | null>(null);
  // Written from an effect (not during render) to satisfy the React Compiler;
  // `run` only fires on events/timers, which happen after effects commit.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    pendingArgs.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    (...args: A) => {
      clearTimeout(timer.current);
      pendingArgs.current = args;
      timer.current = setTimeout(() => {
        pendingArgs.current = null;
        callbackRef.current(...args);
      }, delayMs);
    },
    [delayMs],
  );

  const flush = useCallback(() => {
    const args = pendingArgs.current;
    if (args === null) {
      return false;
    }
    cancel();
    callbackRef.current(...args);
    return true;
  }, [cancel]);

  return { run, cancel, flush };
}
